import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, RefreshCw, X, Tag } from "lucide-react";
import { safeFetch } from "@/api/safeFetch";

interface Promocion {
  codigo_promocional_id: number;
  codigo: string;
  porcentaje_descuento: number;
  fecha_expiracion: string | null;
  profesion_id: number | null;
  tipo_promocion: "AFILIADO" | "GENERAL";
  usos: number;
}

interface Profesion {
  profesion_id: number;
  nombre_profesion: string;
}

interface FormularioPromocion {
  codigo: string;
  porcentaje_descuento: string;
  fecha_expiracion: string;
  profesion_id: string;
}

const formularioVacio: FormularioPromocion = {
  codigo: "",
  porcentaje_descuento: "10",
  fecha_expiracion: "",
  profesion_id: "",
};

export default function PromocionesGenerales() {
  const [promociones, setPromociones] = useState<Promocion[]>([]);
  const [profesiones, setProfesiones] = useState<Profesion[]>([]);
  const [formulario, setFormulario] =
    useState<FormularioPromocion>(formularioVacio);
  const [promocionEditando, setPromocionEditando] = useState<Promocion | null>(
    null,
  );
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [eliminandoId, setEliminandoId] = useState<number | null>(null);
  const [busqueda, setBusqueda] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    setError("");

    try {
      const [listaPromociones, listaProfesiones] = await Promise.all([
        safeFetch.json<Promocion[]>("/api/admin/promociones"),
        safeFetch.json<Profesion[]>("/api/profesiones"),
      ]);

      setPromociones(Array.isArray(listaPromociones) ? listaPromociones : []);
      setProfesiones(Array.isArray(listaProfesiones) ? listaProfesiones : []);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "No se pudieron cargar las promociones.",
      );
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargarDatos();
  }, [cargarDatos]);

  const abrirNueva = () => {
    setPromocionEditando(null);
    setFormulario(formularioVacio);
    setMensaje("");
    setError("");
    setMostrarFormulario(true);
  };

  const abrirEdicion = (promocion: Promocion) => {
    setPromocionEditando(promocion);
    setFormulario({
      codigo: promocion.codigo,
      porcentaje_descuento: String(promocion.porcentaje_descuento),
      fecha_expiracion: promocion.fecha_expiracion?.slice(0, 10) ?? "",
      profesion_id:
        promocion.profesion_id === null ? "" : String(promocion.profesion_id),
    });
    setMensaje("");
    setError("");
    setMostrarFormulario(true);
  };

  const cerrarFormulario = () => {
    if (guardando) return;
    setMostrarFormulario(false);
    setPromocionEditando(null);
    setFormulario(formularioVacio);
  };

  const actualizarCampo = (campo: keyof FormularioPromocion, valor: string) => {
    setFormulario((actual) => ({ ...actual, [campo]: valor }));
  };

  const guardarPromocion = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setMensaje("");

    const codigo = formulario.codigo.trim().toUpperCase();
    const porcentaje = Number(formulario.porcentaje_descuento);

    if (!promocionEditando && !codigo) {
      setError("Introduce el código promocional.");
      return;
    }

    if (!Number.isFinite(porcentaje) || porcentaje <= 0 || porcentaje > 100) {
      setError("El descuento debe ser mayor que 0 y como máximo del 100%.");
      return;
    }

    if (!formulario.fecha_expiracion) {
      setError("Selecciona la fecha de caducidad.");
      return;
    }

    setGuardando(true);

    try {
      const datos = {
        porcentaje_descuento: porcentaje,
        fecha_expiracion: formulario.fecha_expiracion,
        profesion_id: formulario.profesion_id || null,
        ...(promocionEditando ? {} : { codigo }),
      };

      const endpoint = promocionEditando
        ? `/api/admin/promociones/${promocionEditando.codigo_promocional_id}`
        : "/api/admin/promociones";

      await safeFetch.json(endpoint, {
        method: promocionEditando ? "PUT" : "POST",
        body: datos,
      });

      setMostrarFormulario(false);
      setPromocionEditando(null);
      setFormulario(formularioVacio);
      setMensaje(
        promocionEditando
          ? "Promoción actualizada correctamente."
          : "Promoción creada correctamente.",
      );

      await cargarDatos();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "No se pudo guardar la promoción.",
      );
    } finally {
      setGuardando(false);
    }
  };

  const eliminarPromocion = async (promocion: Promocion) => {
    const confirmado = window.confirm(
      `¿Seguro que quieres eliminar la promoción ${promocion.codigo}? Esta acción no se puede deshacer.`,
    );

    if (!confirmado) return;

    setEliminandoId(promocion.codigo_promocional_id);
    setError("");
    setMensaje("");

    try {
      await safeFetch.json(
        `/api/admin/promociones/${promocion.codigo_promocional_id}`,
        { method: "DELETE" },
      );

      setMensaje(`Promoción ${promocion.codigo} eliminada correctamente.`);
      await cargarDatos();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "No se pudo eliminar la promoción.",
      );
    } finally {
      setEliminandoId(null);
    }
  };

  const promocionesFiltradas = promociones.filter((promocion) => {
    const texto = busqueda.trim().toLowerCase();
    if (!texto) return true;

    const nombreProfesion =
      profesiones.find(
        (profesion) => profesion.profesion_id === promocion.profesion_id,
      )?.nombre_profesion ?? "";

    return (
      promocion.codigo.toLowerCase().includes(texto) ||
      nombreProfesion.toLowerCase().includes(texto)
    );
  });

  const obtenerNombreProfesion = (profesionId: number | null) => {
    if (profesionId === null) return "Todas las profesiones";

    return (
      profesiones.find((profesion) => profesion.profesion_id === profesionId)
        ?.nombre_profesion ?? `Profesión #${profesionId}`
    );
  };

  const fechaCaducada = (fecha: string | null) => {
    if (!fecha) return false;

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    const caducidad = new Date(`${fecha.slice(0, 10)}T00:00:00`);
    return caducidad < hoy;
  };

  return (
    <main className="mx-auto min-w-0 w-full max-w-7xl px-3 pb-8 pt-20 sm:px-5 sm:pb-10 lg:px-8">
      {/* ENCABEZADO */}
      <div className="mb-6 flex min-w-0 flex-col gap-4 sm:mb-8 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="mb-2 flex items-center gap-3">
            <div className="shrink-0 rounded-xl bg-indigo-100 p-2.5 text-indigo-700 sm:p-3">
              <Tag size={22} />
            </div>

            <h1 className="text-xl font-bold leading-tight text-slate-900 sm:text-2xl lg:text-3xl">
              Promociones generales
            </h1>
          </div>

          <p className="max-w-2xl text-sm leading-6 text-slate-600">
            Crea y administra códigos de descuento independientes de los códigos
            de afiliados.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap lg:shrink-0 lg:justify-end">
          <button
            type="button"
            onClick={() => void cargarDatos()}
            disabled={cargando}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw size={17} className={cargando ? "animate-spin" : ""} />
            Actualizar
          </button>

          <button
            type="button"
            onClick={abrirNueva}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700"
          >
            <Plus size={18} />
            Nueva promoción
          </button>
        </div>
      </div>

      {/* ESTADÍSTICAS */}
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <p className="text-sm text-slate-500">Promociones registradas</p>
          <p className="mt-1.5 text-2xl font-bold text-slate-900 sm:text-3xl">
            {promociones.length}
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <p className="text-sm text-slate-500">Promociones vigentes</p>
          <p className="mt-1.5 text-2xl font-bold text-emerald-700 sm:text-3xl">
            {
              promociones.filter(
                (promocion) => !fechaCaducada(promocion.fecha_expiracion),
              ).length
            }
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <p className="text-sm text-slate-500">Pagos asociados</p>
          <p className="mt-1.5 text-2xl font-bold text-indigo-700 sm:text-3xl">
            {promociones.reduce(
              (total, promocion) => total + Number(promocion.usos || 0),
              0,
            )}
          </p>
        </div>
      </div>

      {/* MENSAJES */}
      {mensaje && (
        <div
          role="status"
          className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 sm:p-4"
        >
          <span className="min-w-0 break-words">{mensaje}</span>
          <button
            type="button"
            onClick={() => setMensaje("")}
            aria-label="Cerrar mensaje"
            className="shrink-0 rounded-md p-1 hover:bg-emerald-100"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {error && !mostrarFormulario && (
        <div
          role="alert"
          className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 sm:p-4"
        >
          <span className="min-w-0 break-words">{error}</span>
          <button
            type="button"
            onClick={() => setError("")}
            aria-label="Cerrar error"
            className="shrink-0 rounded-md p-1 hover:bg-red-100"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* LISTADO */}
      <section className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 p-4 sm:p-5">
          <label
            htmlFor="buscar-promocion"
            className="mb-2 block text-sm font-medium text-slate-700"
          >
            Buscar promoción
          </label>

          <input
            id="buscar-promocion"
            type="search"
            value={busqueda}
            onChange={(event) => setBusqueda(event.target.value)}
            placeholder="Buscar por código o profesión..."
            className="w-full max-w-lg rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 sm:px-4"
          />
        </div>

        {cargando ? (
          <div className="p-10 text-center text-sm text-slate-500 sm:p-12">
            <RefreshCw
              size={22}
              className="mx-auto mb-3 animate-spin text-indigo-600"
            />
            Cargando promociones...
          </div>
        ) : promocionesFiltradas.length === 0 ? (
          <div className="p-8 text-center sm:p-12">
            <Tag className="mx-auto mb-3 text-slate-300" size={36} />
            <p className="font-semibold text-slate-800">
              No hay promociones para mostrar
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Crea una promoción o modifica el texto de búsqueda.
            </p>
          </div>
        ) : (
          <>
            {/* TABLA PARA TABLET Y ESCRITORIO */}
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full min-w-[850px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3.5 lg:px-5">Código</th>
                    <th className="px-4 py-3.5 lg:px-5">Descuento</th>
                    <th className="px-4 py-3.5 lg:px-5">Profesión</th>
                    <th className="px-4 py-3.5 lg:px-5">Caducidad</th>
                    <th className="px-4 py-3.5 lg:px-5">Pagos</th>
                    <th className="px-4 py-3.5 lg:px-5">Estado</th>
                    <th className="px-4 py-3.5 text-right lg:px-5">Acciones</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {promocionesFiltradas.map((promocion) => {
                    const caducada = fechaCaducada(promocion.fecha_expiracion);

                    return (
                      <tr
                        key={promocion.codigo_promocional_id}
                        className="transition-colors hover:bg-slate-50"
                      >
                        <td className="px-4 py-4 lg:px-5">
                          <span className="inline-block rounded-md bg-indigo-50 px-2.5 py-1.5 font-bold tracking-wide text-indigo-800">
                            {promocion.codigo}
                          </span>
                        </td>

                        <td className="whitespace-nowrap px-4 py-4 font-semibold text-slate-800 lg:px-5">
                          {Number(promocion.porcentaje_descuento)}%
                        </td>

                        <td className="px-4 py-4 text-slate-600 lg:px-5">
                          {obtenerNombreProfesion(promocion.profesion_id)}
                        </td>

                        <td className="whitespace-nowrap px-4 py-4 text-slate-600 lg:px-5">
                          {promocion.fecha_expiracion
                            ? promocion.fecha_expiracion.slice(0, 10)
                            : "Sin fecha"}
                        </td>

                        <td className="px-4 py-4 text-slate-700 lg:px-5">
                          {Number(promocion.usos || 0)}
                        </td>

                        <td className="px-4 py-4 lg:px-5">
                          <span
                            className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${
                              caducada
                                ? "bg-red-100 text-red-700"
                                : "bg-emerald-100 text-emerald-700"
                            }`}
                          >
                            {caducada ? "Caducada" : "Vigente"}
                          </span>
                        </td>

                        <td className="px-4 py-4 lg:px-5">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => abrirEdicion(promocion)}
                              title="Editar promoción"
                              aria-label={`Editar ${promocion.codigo}`}
                              className="rounded-lg border border-slate-200 p-2 text-indigo-700 transition-colors hover:bg-indigo-50"
                            >
                              <Pencil size={16} />
                            </button>

                            <button
                              type="button"
                              onClick={() => void eliminarPromocion(promocion)}
                              disabled={
                                eliminandoId === promocion.codigo_promocional_id
                              }

                              title="Eliminar promoción"

                              aria-label={`Eliminar ${promocion.codigo}`}
                              className="rounded-lg border border-slate-200 p-2 text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {eliminandoId ===
                              promocion.codigo_promocional_id ? (
                                <RefreshCw className="animate-spin" size={16} />
                              ) : (
                                <Trash2 size={16} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* TARJETAS PARA MÓVIL */}
            <div className="divide-y divide-slate-100 md:hidden">
              {promocionesFiltradas.map((promocion) => {
                const caducada = fechaCaducada(promocion.fecha_expiracion);

                return (
                  <article
                    key={promocion.codigo_promocional_id}
                    className="space-y-3 p-4"
                  >
                    <div className="flex min-w-0 items-start justify-between gap-3">
                      <div className="min-w-0">
                        <span className="inline-block max-w-full break-all rounded-md bg-indigo-50 px-2.5 py-1.5 text-sm font-bold tracking-wide text-indigo-800">
                          {promocion.codigo}
                        </span>

                        <p className="mt-2 text-lg font-bold text-slate-900">
                          {Number(promocion.porcentaje_descuento)}% de descuento
                        </p>
                      </div>

                      <span
                        className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
                          caducada
                            ? "bg-red-100 text-red-700"
                            : "bg-emerald-100 text-emerald-700"
                        }`}
                      >
                        {caducada ? "Caducada" : "Vigente"}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                      <div className="min-w-0">
                        <p className="text-xs text-slate-500">Profesión</p>
                        <p className="mt-1 break-words font-medium text-slate-700">
                          {obtenerNombreProfesion(promocion.profesion_id)}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-slate-500">Caducidad</p>
                        <p className="mt-1 font-medium text-slate-700">
                          {promocion.fecha_expiracion
                            ? promocion.fecha_expiracion.slice(0, 10)
                            : "Sin fecha"}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
                      <button
                        type="button"
                        onClick={() => abrirEdicion(promocion)}
                        aria-label={`Editar ${promocion.codigo}`}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
                      >
                        <Pencil size={15} />
                        Editar
                      </button>

                      <button
                        type="button"
                        onClick={() => void eliminarPromocion(promocion)}
                        disabled={
                          eliminandoId === promocion.codigo_promocional_id
                        }

                        title="Eliminar promoción"

                        aria-label={`Eliminar ${promocion.codigo}`}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {eliminandoId === promocion.codigo_promocional_id ? (
                          <RefreshCw className="animate-spin" size={15} />
                        ) : (
                          <Trash2 size={15} />
                        )}
                        Eliminar
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </section>

      {/* MODAL COMPACTO Y RESPONSIVE */}
      {mostrarFormulario && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/50 p-3 sm:p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) cerrarFormulario();
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="titulo-formulario-promocion"
            className="my-auto flex max-h-[calc(100dvh-1.5rem)] w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-2xl sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl"
          >
            {/* CABECERA DEL MODAL */}
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-4 py-3.5 sm:px-5">
              <div className="min-w-0">
                <h2
                  id="titulo-formulario-promocion"
                  className="text-lg font-bold text-slate-900"
                >
                  {promocionEditando ? "Editar promoción" : "Crear promoción"}
                </h2>

                <p className="mt-1 text-xs text-slate-500 sm:text-sm">
                  Configura el descuento y a quién se aplica.
                </p>
              </div>

              <button
                type="button"
                onClick={cerrarFormulario}
                disabled={guardando}
                aria-label="Cerrar formulario"
                className="shrink-0 rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>

            {/* FORMULARIO CON SCROLL INTERNO */}
            <form
              onSubmit={guardarPromocion}
              className="min-h-0 space-y-4 overflow-y-auto overscroll-contain p-4 sm:space-y-4 sm:p-5"
            >
              <div>
                <label
                  htmlFor="codigo-promocional"
                  className="mb-1.5 block text-sm font-medium text-slate-700"
                >
                  Código promocional
                </label>

                <input
                  id="codigo-promocional"
                  type="text"
                  required
                  maxLength={50}
                  value={formulario.codigo}
                  disabled={Boolean(promocionEditando) || guardando}
                  onChange={(event) =>
                    actualizarCampo("codigo", event.target.value.toUpperCase())
                  }
                  placeholder="Ej. BIENVENIDA20"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm uppercase outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-100"
                />

                {promocionEditando && (
                  <p className="mt-1 text-xs text-slate-500">
                    El código no se puede modificar después de crearlo.
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="porcentaje-descuento"
                  className="mb-1.5 block text-sm font-medium text-slate-700"
                >
                  Porcentaje de descuento
                </label>

                <div className="relative">
                  <input
                    id="porcentaje-descuento"
                    type="number"
                    required
                    min="0.01"
                    max="100"
                    step="0.01"
                    value={formulario.porcentaje_descuento}
                    disabled={guardando}
                    onChange={(event) =>
                      actualizarCampo(
                        "porcentaje_descuento",
                        event.target.value,
                      )
                    }
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 pr-10 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-100"
                  />

                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-500">
                    %
                  </span>
                </div>
              </div>

              <div>
                <label
                  htmlFor="fecha-expiracion"
                  className="mb-1.5 block text-sm font-medium text-slate-700"
                >
                  Fecha de caducidad
                </label>

                <input
                  id="fecha-expiracion"
                  type="date"
                  required
                  value={formulario.fecha_expiracion}
                  disabled={guardando}
                  onChange={(event) =>
                    actualizarCampo("fecha_expiracion", event.target.value)
                  }
                  className="w-full min-w-0 rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-100"
                />
              </div>

              <div>
                <label
                  htmlFor="profesion-promocion"
                  className="mb-1.5 block text-sm font-medium text-slate-700"
                >
                  Profesión a la que se aplica
                </label>

                <select
                  id="profesion-promocion"
                  value={formulario.profesion_id}
                  disabled={guardando}
                  onChange={(event) =>
                    actualizarCampo("profesion_id", event.target.value)
                  }
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-100"
                >
                  <option value="">Todas las profesiones</option>

                  {profesiones.map((profesion) => (
                    <option
                      key={profesion.profesion_id}
                      value={profesion.profesion_id}
                    >
                      {profesion.nombre_profesion}
                    </option>
                  ))}
                </select>

                <p className="mt-1 text-xs leading-5 text-slate-500">
                  Si eliges una profesión, el código queda limitado a ella.
                </p>
              </div>

              {error && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                >
                  {error}
                </div>
              )}

              {/* BOTONES */}
              <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={cerrarFormulario}
                  disabled={guardando}
                  className="inline-flex items-center justify-center rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  disabled={guardando}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {guardando && (
                    <RefreshCw size={16} className="animate-spin" />
                  )}

                  {guardando
                    ? "Guardando..."
                    : promocionEditando
                      ? "Guardar cambios"
                      : "Crear promoción"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </main>
  );
}
